/* istanbul ignore file */

/* tslint:disable */

/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise'
import { OpenAPI } from '../core/OpenAPI'
import { request as __request } from '../core/request'
import type { PatchedOdysseyRequest } from '../index'
import type { CleaningZone } from '../models/CleaningZone'
import type { CleaningZoneRead } from '../models/CleaningZoneRead'
import type { CleaningZoneRequest } from '../models/CleaningZoneRequest'
import { EventStatisticsResponse, OdysseyEventStatisticsResponse } from '../models/EventStatistics'
import type { FileData } from '../models/FileData'
import type { FileRequest } from '../models/FileRequest'
import type { Mission } from '../models/Mission'
import type { MissionLayerUploadFileRequest } from '../models/MissionLayerUploadFileRequest'
import type { MissionMapLayer } from '../models/MissionMapLayer'
import type { MissionMapLayerRead } from '../models/MissionMapLayerRead'
import type { MissionMapLayerRequest } from '../models/MissionMapLayerRequest'
import type { MissionPointTransferRoute } from '../models/MissionPointTransferRoute'
import type { MissionPointTransferRouteRead } from '../models/MissionPointTransferRouteRead'
import type { MissionPointTransferRouteRequest } from '../models/MissionPointTransferRouteRequest'
import type { MissionRead } from '../models/MissionRead'
import type { MissionRequest } from '../models/MissionRequest'
import type { MissionStatus } from '../models/MissionStatus'
import type { MissionXyzLayer } from '../models/MissionXyzLayer'
import type { MissionXyzLayerRead } from '../models/MissionXyzLayerRead'
import type { MissionXyzLayerRequest } from '../models/MissionXyzLayerRequest'
import type { Odyssey } from '../models/Odyssey'
import type { OdysseyRead } from '../models/OdysseyRead'
import type { OdysseyRequest } from '../models/OdysseyRequest'
import type { PaginatedMissionMapLayerReadList } from '../models/PaginatedMissionMapLayerReadList'
import type { PaginatedMissionPointTransferRouteReadList } from '../models/PaginatedMissionPointTransferRouteReadList'
import type { PaginatedMissionReadListList } from '../models/PaginatedMissionReadListList'
import type { PaginatedMissionXyzLayerReadList } from '../models/PaginatedMissionXyzLayerReadList'
import type { PaginatedOdysseyReadList } from '../models/PaginatedOdysseyReadList'
import type { PaginatedRecordedRouteReadList } from '../models/PaginatedRecordedRouteReadList'
import type { PaginatedRestrictZoneReadList } from '../models/PaginatedRestrictZoneReadList'
import type { PaginatedRobotLotReadList } from '../models/PaginatedRobotLotReadList'
import type { PaginatedRobotReadList } from '../models/PaginatedRobotReadList'
import type { PaginatedRobotStationReadList } from '../models/PaginatedRobotStationReadList'
import type { PaginatedTransferRouteReadList } from '../models/PaginatedTransferRouteReadList'
import type { PatchedCleaningZoneRequest } from '../models/PatchedCleaningZoneRequest'
import type { PatchedMissionMapLayerRequest } from '../models/PatchedMissionMapLayerRequest'
import type { PatchedMissionPointTransferRouteRequest } from '../models/PatchedMissionPointTransferRouteRequest'
import type { PatchedMissionRequest } from '../models/PatchedMissionRequest'
import type { PatchedMissionXyzLayerRequest } from '../models/PatchedMissionXyzLayerRequest'
import type { PatchedRestrictZoneRequest } from '../models/PatchedRestrictZoneRequest'
import type { PatchedRobotRequest } from '../models/PatchedRobotRequest'
import type { PatchedRobotStationRequest } from '../models/PatchedRobotStationRequest'
import type { PatchedTransferRouteRequest } from '../models/PatchedTransferRouteRequest'
import type { RecordedRouteRead } from '../models/RecordedRouteRead'
import type { RestrictZone } from '../models/RestrictZone'
import type { RestrictZoneRead } from '../models/RestrictZoneRead'
import type { RestrictZoneRequest } from '../models/RestrictZoneRequest'
import type { Robot } from '../models/Robot'
import type { RobotRead } from '../models/RobotRead'
import type { RobotRequest } from '../models/RobotRequest'
import type { RobotStation } from '../models/RobotStation'
import type { RobotStationRead } from '../models/RobotStationRead'
import type { RobotStationRequest } from '../models/RobotStationRequest'
import type { SchedulerSettings } from '../models/SchedulerSettings'
import type { TransferRoute } from '../models/TransferRoute'
import type { TransferRouteRead } from '../models/TransferRouteRead'
import type { TransferRouteRequest } from '../models/TransferRouteRequest'
import { MultiLineString, Polygon } from 'geojson'

export class MainService {
  /**
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedMissionReadListList
   * @throws ApiError
   */
  public static mainCleaningZoneList(
    missionId?: Array<number>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedMissionReadListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/cleaning-zone/',
      query: {
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns CleaningZone
   * @throws ApiError
   */
  public static mainCleaningZoneCreate(requestBody: CleaningZoneRequest): CancelablePromise<CleaningZone> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/cleaning-zone/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param string
   * @returns CleaningZone
   * @throws ApiError
   */
  public static mainGetPolygonFromFile(filename: string): CancelablePromise<Polygon> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/get-polygon/',
      query: {
        filename: filename,
      },
    })
  }

  /**
   * @param string
   * @returns CleaningZone
   * @throws ApiError
   */
  public static mainGetRouteFromFile(filename: string): CancelablePromise<MultiLineString> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/get-route-from-file/',
      query: {
        filename: filename,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this cleaning zone.
   * @returns CleaningZoneRead
   * @throws ApiError
   */
  public static mainCleaningZoneRetrieve(id: number): CancelablePromise<CleaningZoneRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/cleaning-zone/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this cleaning zone.
   * @param requestBody
   * @returns CleaningZone
   * @throws ApiError
   */
  public static mainCleaningZonePartialUpdate(
    id: number,
    requestBody?: PatchedCleaningZoneRequest
  ): CancelablePromise<CleaningZoneRead> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/cleaning-zone/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this cleaning zone.
   * @param requestBody
   * @returns CleaningZone
   * @throws ApiError
   */
  public static mainCleaningZoneCleaningTypeUpdate(
    id: number,
    requestBody?: PatchedCleaningZoneRequest
  ): CancelablePromise<CleaningZone> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/cleaning-zone/{id}/cleaning-type/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this cleaning zone.
   * @returns void
   * @throws ApiError
   */
  public static mainCleaningZoneDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/cleaning-zone/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this cleaning zone.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainCleaningZoneStartCalculationCreate(id: number): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/cleaning-zone/{id}/start-calculation/',
      path: {
        id: id,
      },
      body: {},
    })
  }

  /**
   * Copies a cleaning zone to another mission.
   * @param id The ID of the cleaning zone to copy.
   * @param missionId The ID of the target mission.
   * @returns CleaningZone The newly created cleaning zone.
   * @throws ApiError
   */
  public static mainCleaningZoneCopy(id: number, missionId: number): CancelablePromise<CleaningZone> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/cleaning-zone/{id}/copy-to-mission/{mission_id}/',
      path: {
        id: id,
        mission_id: missionId,
      },
    })
  }

  /**
   * Moves a cleaning zone in the mission's zone order.
   * @param id The ID of the cleaning zone to move.
   * @param up The moving direction flag
   * @returns CleaningZone The updated cleaning zone.
   * @throws ApiError
   */
  public static mainMoveZone(id: number, up: boolean): CancelablePromise<CleaningZone> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/cleaning-zone/{id}/move/',
      path: {
        id: id,
      },
      body: {
        direction: up ? 'up' : 'down',
      },
    })
  }

  /**
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedMissionReadListList
   * @throws ApiError
   */
  public static mainMissionList(
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string,
    robotType?: string,
    groupId?: number
  ): CancelablePromise<PaginatedMissionReadListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission/',
      query: {
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
        robot_type: robotType,
        group: groupId,
      },
    })
  }

  /**
   * @param requestBody
   * @returns Mission
   * @throws ApiError
   */
  public static mainMissionCreate(requestBody?: MissionRequest): CancelablePromise<Mission> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param requestBody
   * @returns Odyssey
   * @throws ApiError
   */
  public static mainOdysseyCreate(requestBody: OdysseyRequest): CancelablePromise<Odyssey> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/odyssey/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param missionIdArray
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param robotId
   * @param search A search term.
   * @param status
   * @returns PaginatedOdysseyReadList
   * @throws ApiError
   */
  public static mainOdysseyList(
    missionIdArray?: Array<number>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    robotId?: Array<number>,
    search?: string,
    status?: 'new' | 'scenario_calculating' | 'scenario_done' | 'scenario_error'
  ): CancelablePromise<PaginatedOdysseyReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/odyssey/',
      query: {
        robot_id: robotId,
        search: search,
        status: status,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this odyssey.
   * @returns OdysseyRead
   * @throws ApiError
   */
  public static mainOdysseyRetrieve(id: number): CancelablePromise<OdysseyRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/odyssey/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this odyssey.
   * @returns void
   * @throws ApiError
   */
  public static mainOdysseyDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/odyssey/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this odyssey.
   * @param requestBody
   * @returns Odyssey
   * @throws ApiError
   */
  public static mainOdysseyPartialUpdate(id: number, requestBody?: PatchedOdysseyRequest): CancelablePromise<Odyssey> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/odyssey/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this odyssey.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainOdysseyStartCalculationCreate(id: number): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/odyssey/{id}/start-calculation/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @returns FileData[]
   * @throws ApiError
   */
  public static mainListGeoTiffFiles(): CancelablePromise<Array<FileData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/geotiff-files/',
    })
  }

  /**
   * @returns FileData[]
   * @throws ApiError
   */
  public static mainListRecordedRoutes(): CancelablePromise<Array<RecordedRouteRead>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/recorded-route-list/',
    })
  }

  /**
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedRecordedRouteReadList
   * @throws ApiError
   */
  public static mainRecordedRouteList(
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedRecordedRouteReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/recorded-route/',
      query: {
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this recorded route.
   * @returns RecordedRouteRead
   * @throws ApiError
   */
  public static mainRecordedRouteRetrieve(id: number): CancelablePromise<RecordedRouteRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/recorded-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this recorded route.
   * @returns void
   * @throws ApiError
   */
  public static mainRecordedRouteDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/recorded-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this recorded route.
   * @param requestBody
   * @returns RecordedRouteRead
   * @throws ApiError
   */
  public static mainRecordedRoutePartialUpdate(
    id: number,
    requestBody?: { name?: string | null }
  ): CancelablePromise<RecordedRouteRead> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/recorded-route/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @returns void
   * @param requestBody Contains a filename.
   * @throws ApiError
   */
  public static deleteRecordedRouteZone(requestBody: FileRequest): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/delete-route/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @returns void
   * @param requestBody Contains a filename and a new_filename.
   * @throws ApiError
   */
  public static renameRecordedRouteZone(requestBody: FileRequest): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/rename-route/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * Uploads a GeoTIFF to a specified layer by filename.
   * @param layerId The ID of the existing layer.
   * @param requestBody Contains the filename of the GeoTIFF.
   * @returns any
   * @throws ApiError
   */
  public static mainMissionXyzLayerUploadFileByName(layerId: number, requestBody: FileRequest): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: `/api/main/mission-xyz-layer/{layer_id}/upload-file-by-name/`,
      path: {
        layer_id: layerId,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedMissionMapLayerReadList
   * @throws ApiError
   */
  public static mainMissionMapLayerList(
    missionId?: Array<number>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedMissionMapLayerReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-map-layer/',
      query: {
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns MissionMapLayer
   * @throws ApiError
   */
  public static mainMissionMapLayerCreate(requestBody: MissionMapLayerRequest): CancelablePromise<MissionMapLayer> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-map-layer/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission map.
   * @returns MissionMapLayerRead
   * @throws ApiError
   */
  public static mainMissionMapLayerRetrieve(id: number): CancelablePromise<MissionMapLayerRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-map-layer/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission map.
   * @param requestBody
   * @returns MissionMapLayer
   * @throws ApiError
   */
  public static mainMissionMapLayerPartialUpdate(
    id: number,
    requestBody?: PatchedMissionMapLayerRequest
  ): CancelablePromise<MissionMapLayer> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/mission-map-layer/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission map.
   * @returns void
   * @throws ApiError
   */
  public static mainMissionMapLayerDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/mission-map-layer/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission map.
   * @param formData
   * @returns MissionMapLayerRead
   * @throws ApiError
   */
  public static mainMissionMapLayerUploadFileCreate(
    id: number,
    formData: MissionLayerUploadFileRequest
  ): CancelablePromise<MissionMapLayerRead> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-map-layer/{id}/upload-file/',
      path: {
        id: id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    })
  }

  /**
   * @param cleaningZone
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedMissionPointTransferRouteReadList
   * @throws ApiError
   */
  public static mainMissionPointTransferRouteList(
    cleaningZone?: Array<number>,
    missionId?: Array<number>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedMissionPointTransferRouteReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-point-transfer-route/',
      query: {
        cleaning_zone: cleaningZone,
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns MissionPointTransferRoute
   * @throws ApiError
   */
  public static mainMissionPointTransferRouteCreate(
    requestBody: MissionPointTransferRouteRequest
  ): CancelablePromise<MissionPointTransferRoute> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-point-transfer-route/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission point transfer route.
   * @returns MissionPointTransferRouteRead
   * @throws ApiError
   */
  public static mainMissionPointTransferRouteRetrieve(id: number): CancelablePromise<MissionPointTransferRouteRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-point-transfer-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission point transfer route.
   * @param requestBody
   * @returns MissionPointTransferRoute
   * @throws ApiError
   */
  public static mainMissionPointTransferRoutePartialUpdate(
    id: number,
    requestBody?: PatchedMissionPointTransferRouteRequest
  ): CancelablePromise<MissionPointTransferRoute> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/mission-point-transfer-route/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission point transfer route.
   * @returns void
   * @throws ApiError
   */
  public static mainMissionPointTransferRouteDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/mission-point-transfer-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission point transfer route.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainMissionPointTransferRouteStartCalculationCreate(id: number): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-point-transfer-route/{id}/start-calculation/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedMissionXyzLayerReadList
   * @throws ApiError
   */
  public static mainMissionXyzLayerList(
    missionId?: Array<number>,
    ordering?: Array<
      | 'id'
      | '-id'
      | 'name'
      | '-name'
      | 'created_at'
      | '-created_at'
      | 'updated_at'
      | '-updated_at'
      | 'sorting'
      | '-sorting'
    >,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedMissionXyzLayerReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-xyz-layer/',
      query: {
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns MissionXyzLayer
   * @throws ApiError
   */
  public static mainMissionXyzLayerCreate(requestBody: MissionXyzLayerRequest): CancelablePromise<MissionXyzLayer> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-xyz-layer/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission XYZ layer.
   * @returns MissionXyzLayerRead
   * @throws ApiError
   */
  public static mainMissionXyzLayerRetrieve(id: number): CancelablePromise<MissionXyzLayerRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission-xyz-layer/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission XYZ layer.
   * @param requestBody
   * @returns MissionXyzLayer
   * @throws ApiError
   */
  public static mainMissionXyzLayerPartialUpdate(
    id: number,
    requestBody?: PatchedMissionXyzLayerRequest
  ): CancelablePromise<MissionXyzLayer> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/mission-xyz-layer/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission XYZ layer.
   * @returns void
   * @throws ApiError
   */
  public static mainMissionXyzLayerDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/mission-xyz-layer/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission XYZ layer.
   * @param formData
   * @returns MissionXyzLayerRead
   * @throws ApiError
   */
  public static mainMissionXyzLayerUploadFileCreate(
    id: number,
    formData: MissionLayerUploadFileRequest
  ): CancelablePromise<MissionXyzLayerRead> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission-xyz-layer/{id}/upload-file/',
      path: {
        id: id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    })
  }

  /**
   * @param id A unique integer value identifying this mission.
   * @returns MissionRead
   * @throws ApiError
   */
  public static mainMissionRetrieve(id: number): CancelablePromise<MissionRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission.
   * @param requestBody
   * @returns Mission
   * @throws ApiError
   */
  public static mainMissionPartialUpdate(id: number, requestBody?: PatchedMissionRequest): CancelablePromise<Mission> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/mission/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this mission.
   * @returns void
   * @throws ApiError
   */
  public static mainMissionDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/mission/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this mission.
   * @returns void
   * @throws ApiError
   */
  public static mainMissionCopy(id: number): CancelablePromise<Mission> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission/{id}/copy/',
      path: {
        id: id,
      },
    })
  }

  /**--
   * @param id A unique integer value identifying this mission.
   * @returns MissionStatus
   * @throws ApiError
   */
  public static mainMissionGetStatusRetrieve(id: number): CancelablePromise<MissionStatus> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/mission/{id}/get-status/',
      path: {
        id: id,
      },
    })
  }

  /**
   * To force recalculation of all routes, send `?force=1`
   * @param id A unique integer value identifying this mission.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainMissionStartCalculationCreate(id: number): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/mission/{id}/start-calculation/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedRestrictZoneReadList
   * @throws ApiError
   */
  public static mainRestrictZoneList(
    missionId?: Array<number>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedRestrictZoneReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/restrict-zone/',
      query: {
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns RestrictZone
   * @throws ApiError
   */
  public static mainRestrictZoneCreate(requestBody: RestrictZoneRequest): CancelablePromise<RestrictZone> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/restrict-zone/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this restrict zone.
   * @returns RestrictZoneRead
   * @throws ApiError
   */
  public static mainRestrictZoneRetrieve(id: number): CancelablePromise<RestrictZoneRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/restrict-zone/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this restrict zone.
   * @param requestBody
   * @returns RestrictZone
   * @throws ApiError
   */
  public static mainRestrictZonePartialUpdate(
    id: number,
    requestBody?: PatchedRestrictZoneRequest
  ): CancelablePromise<RestrictZone> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/restrict-zone/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this restrict zone.
   * @returns void
   * @throws ApiError
   */
  public static mainRestrictZoneDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/restrict-zone/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedRobotReadList
   * @throws ApiError
   */
  public static mainRobotList(
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string,
    id?: Array<number>,
    vendor?: string
  ): CancelablePromise<PaginatedRobotReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot/',
      query: {
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
        id: id,
        vendor: vendor,
      },
    })
  }

  /**
   * @param requestBody
   * @returns Robot
   * @throws ApiError
   */
  public static mainRobotCreate(requestBody: RobotRequest): CancelablePromise<Robot> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedRobotStationReadList
   * @throws ApiError
   */
  public static mainRobotStationList(
    ordering?: Array<
      | 'id'
      | '-id'
      | 'name'
      | '-name'
      | 'station_id'
      | '-station_id'
      | 'online'
      | '-online'
      | 'created_at'
      | '-created_at'
      | 'updated_at'
      | '-updated_at'
    >,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedRobotStationReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/',
      query: {
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns RobotStation
   * @throws ApiError
   */
  public static mainRobotStationCreate(requestBody?: RobotStationRequest): CancelablePromise<RobotStation> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot-station/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this robot station.
   * @returns RobotStationRead
   * @throws ApiError
   */
  public static mainRobotStationRetrieve(id: number): CancelablePromise<RobotStationRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-station/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this robot station.
   * @param requestBody
   * @returns RobotStation
   * @throws ApiError
   */
  public static mainRobotStationPartialUpdate(
    id: number,
    requestBody?: PatchedRobotStationRequest
  ): CancelablePromise<RobotStation> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/robot-station/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this robot station.
   * @returns void
   * @throws ApiError
   */
  public static mainRobotStationDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/robot-station/{id}/',
      path: {
        id: id,
      },
    })
  }

  public static mainRobotLotList(
    ordering?: Array<
      | 'id'
      | '-id'
      | 'lot_id'
      | '-lot_id'
      | 'robot_sn'
      | '-robot_sn'
      | 'robot_station'
      | '-robot_station'
      | 'state_history'
      | '-state_history'
      | 'created_at'
      | '-created_at'
      | 'updated_at'
      | '-updated_at'
    >,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedRobotLotReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot-lot/',
      query: {
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this robot.
   * @returns RobotRead
   * @throws ApiError
   */
  public static mainRobotRetrieve(id: number): CancelablePromise<RobotRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/robot/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this robot.
   * @param requestBody
   * @returns Robot
   * @throws ApiError
   */
  public static mainRobotPartialUpdate(id: number, requestBody?: PatchedRobotRequest): CancelablePromise<Robot> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/robot/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this robot.
   * @returns void
   * @throws ApiError
   */
  public static mainRobotDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/robot/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param cmd
   * @param id A unique integer value identifying this robot.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainRobotExecCommandCreate(id: number, cmd: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot/{id}/exec-command/{cmd}/',
      path: {
        cmd: cmd,
        id: id,
      },
    })
  }

  /**
   * @param cmd
   * @param id A unique integer value identifying this robot.
   * @param value
   * @returns any No response body
   * @throws ApiError
   */
  public static mainRobotExecEquipmentCommandCreate(cmd: string, id: number, value: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot/{id}/exec-equipment-command/{cmd}/{value}/',
      path: {
        cmd: cmd,
        id: id,
        value: value,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this robot.
   * @param mode
   * @returns any No response body
   * @throws ApiError
   */
  public static mainRobotSwitchModeCreate(id: number, mode: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/robot/{id}/switch-mode/{mode}/',
      path: {
        id: id,
        mode: mode,
      },
    })
  }

  /**
   * @returns any No response body
   * @throws ApiError
   */
  public static mainTestRetrieve(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/test/',
    })
  }

  /**
   * @returns any No response body
   * @throws ApiError
   */
  public static mainTest2Retrieve(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/test2/',
    })
  }

  /**
   * @returns any No response body
   * @throws ApiError
   */
  public static mainTest3Retrieve(): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/test3/',
    })
  }

  /**
   * @param cleaningZoneFromId
   * @param cleaningZoneToId
   * @param missionId
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search A search term.
   * @returns PaginatedTransferRouteReadList
   * @throws ApiError
   */
  public static mainTransferRouteList(
    cleaningZoneFromId?: Array<number>,
    cleaningZoneToId?: Array<number>,
    missionId?: Array<string>,
    ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
    page?: number,
    pageSize?: number,
    search?: string
  ): CancelablePromise<PaginatedTransferRouteReadList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/transfer-route/',
      query: {
        cleaning_zone_from_id: cleaningZoneFromId,
        cleaning_zone_to_id: cleaningZoneToId,
        mission_id: missionId,
        ordering: ordering,
        page: page,
        page_size: pageSize,
        search: search,
      },
    })
  }

  /**
   * @param requestBody
   * @returns TransferRoute
   * @throws ApiError
   */
  public static mainTransferRouteCreate(requestBody?: TransferRouteRequest): CancelablePromise<TransferRoute> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/transfer-route/',
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this transfer route.
   * @returns TransferRouteRead
   * @throws ApiError
   */
  public static mainTransferRouteRetrieve(id: number): CancelablePromise<TransferRouteRead> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/transfer-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this transfer route.
   * @param requestBody
   * @returns TransferRoute
   * @throws ApiError
   */
  public static mainTransferRoutePartialUpdate(
    id: number,
    requestBody?: PatchedTransferRouteRequest
  ): CancelablePromise<TransferRoute> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/transfer-route/{id}/',
      path: {
        id: id,
      },
      body: requestBody,
      mediaType: 'application/json',
    })
  }

  /**
   * @param id A unique integer value identifying this transfer route.
   * @returns void
   * @throws ApiError
   */
  public static mainTransferRouteDestroy(id: number): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/transfer-route/{id}/',
      path: {
        id: id,
      },
    })
  }

  /**
   * @param id A unique integer value identifying this transfer route.
   * @returns any No response body
   * @throws ApiError
   */
  public static mainTransferRouteStartCalculationCreate(id: number): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/transfer-route/{id}/start-calculation/',
      path: {
        id: id,
      },
      body: {},
    })
  }

  /**
   * Get event statistics with optional filters
   * @param params Query parameters for filtering
   * @returns EventStatistics[]
   * @throws ApiError
   */
  public static getEventStatistics(params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }): CancelablePromise<EventStatisticsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/event-statistics/',
      query: params,
    })
  }

  /**
   * Get odyssey event statistics with optional filters
   * @param params Query parameters for filtering
   * @returns OdysseyEventStatistics[]
   * @throws ApiError
   */
  public static getOdysseyEventStatistics(params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }): CancelablePromise<OdysseyEventStatisticsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/odyssey-statistics/',
      query: params,
    })
  }

  /**
   * Get event statistics for specific mission
   * @param missionId Mission ID
   * @returns EventStatistics[]
   * @throws ApiError
   */
  public static getMissionEventStatistics(missionId: number): CancelablePromise<EventStatisticsResponse> {
    return this.getEventStatistics({ mission_id: missionId })
  }

  /**
   * Get event statistics for date range
   * @param startDate Start date in ISO format
   * @param endDate End date in ISO format
   * @returns EventStatistics[]
   * @throws ApiError
   */
  public static getEventStatisticsByDateRange(
    startDate: string,
    endDate: string
  ): CancelablePromise<EventStatisticsResponse> {
    return this.getEventStatistics({
      start_date: startDate,
      end_date: endDate,
    })
  }

  /**
   * Get odyssey event statistics for date range
   * @param startDate Start date in ISO format
   * @param endDate End date in ISO format
   * @returns EventStatistics[]
   * @throws ApiError
   */
  public static getOdysseyEventStatisticsByDateRange(
    startDate: string,
    endDate: string
  ): CancelablePromise<OdysseyEventStatisticsResponse> {
    return this.getOdysseyEventStatistics({
      start_date: startDate,
      end_date: endDate,
    })
  }

  /**
   * Get Gausium task reports for dashboard (from cache, EventStatistics-like format).
   * @param params start_date, end_date (required), robot_sn (optional)
   */
  public static getGausiumTaskReports(params: {
    start_date: string
    end_date: string
    robot_sn?: string
  }): CancelablePromise<{ total: number; results: any[]; gausium_robot_sns: string[] }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-task-reports/',
      query: params,
    })
  }
}
